import clsx from 'clsx';
import { Field, Formik } from 'formik';
import htmlReactParser from 'html-react-parser';
import React, { memo, useEffect, useMemo, useState } from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import useOffers from '@hooks/useOffers';
import { subscribe_promotion_path } from '@routes';
import { ClickTarget, EventProperties, TrackingLocation, click } from '@tracking';
import { OfferOption } from '@utils/backend-api/subscribe';

const OfferPriceDetails = memo(({ offer }: { offer: OfferOption }) => {
  const currentPriceToDisplay = offer.currentPriceDisplayOverride || offer.currentPrice;
  const hasDiscount = offer.originalPrice !== offer.currentPrice;
  const originalPriceToDisplay = offer.originalPriceDisplayOverride || offer.originalPrice;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-8">
        <span className="radiobutton-text font-bold tracking-wider">
          {offer.title.replace('The Information ', '')}
        </span>
        <span>
          <span className="font-medium text-black">${currentPriceToDisplay}</span>{' '}
          {hasDiscount && (
            <span className="text-grayscale-gray line-through">${originalPriceToDisplay}</span>
          )}
          {offer.billingFrequencyOverride && (
            <span className="font-medium text-black">
              {' '}
              {offer.currentPrice != offer.originalPrice && offer.isAnnual
                ? '/ first year'
                : offer.billingFrequencyOverride}
            </span>
          )}
        </span>
      </div>
      <p className="m-0 font-suisse-intl text-description-sm text-grayscale-charcoal">
        {htmlReactParser(offer.shortDescription || '')}
      </p>
      {hasDiscount && offer.originalPrice > 0 && (
        <div className="mt-4 text-grayscale-gray">
          Save {Math.round((1 - offer.currentPrice / offer.originalPrice) * 100)}%
        </div>
      )}
    </div>
  );
});

OfferPriceDetails.displayName = 'OfferPriceDetails';

export interface SubscribeDropdownContentProps {
  className: string;
  analytics?: EventProperties;
}

const ANNUAL_OFFER = 'bc_sub_button_hover_299_2026';
const PRO_ANNUAL_OFFER = 'sub-button-hover-749';

export interface FormValues {
  selectedOffer: string;
}

const INITIAL_VALUES: FormValues = {
  selectedOffer: '',
};

const SubscribeDropdownContent = ({ className, analytics }: SubscribeDropdownContentProps) => {
  const [initialValues, setInitialValues] = useState<FormValues>(INITIAL_VALUES);
  const { data: offersData, isLoading: isLoadingOffer } = useOffers({
    offer_codes: [ANNUAL_OFFER, PRO_ANNUAL_OFFER],
  });
  const { Track, trackEvent } = useEventTracking({
    ...analytics,
    location: TrackingLocation.NavSubscribeDropdown,
  });

  const { annualOfferData, proOfferData } = useMemo(() => {
    if (!offersData || offersData.length === 0) {
      return { annualOfferData: undefined, proOfferData: undefined };
    }
    return {
      annualOfferData: offersData.find((offer) => offer.isAnnual),
      proOfferData: offersData.find((offer) => offer.isPro),
    };
  }, [offersData]);

  useEffect(() => {
    if (annualOfferData?.code) {
      setInitialValues({
        selectedOffer: annualOfferData.code,
      });
    }
  }, [annualOfferData?.code]);

  const offerOptions = useMemo(() => {
    if (!annualOfferData || !proOfferData) return [];

    return [
      {
        labelClassName: '!items-start text-primary-magenta flex-shrink-0',
        radioButtonContainerClassName: 'flex-shrink-0',
        label: annualOfferData.title,
        value: annualOfferData.code,
        children: <OfferPriceDetails offer={annualOfferData} />,
      },
      {
        labelClassName: 'text-pro-gold !items-start flex-shrink-0',
        radioButtonContainerClassName: 'flex-shrink-0',
        radioButtonClassName: '!bg-pro-gold',
        label: proOfferData.title,
        value: proOfferData.code,
        children: <OfferPriceDetails offer={proOfferData} />,
      },
    ];
  }, [annualOfferData, proOfferData]);

  if (
    isLoadingOffer ||
    !offersData ||
    offersData.length === 0 ||
    !proOfferData ||
    !annualOfferData
  ) {
    return null;
  }

  return (
    <Track>
      <aside aria-live="polite" className={className}>
        <h2 className="m-0 font-suisse-works text-headline-xxs text-black">
          Subscribe to The Information
        </h2>
        <div className="mt-16">
          <Formik enableReinitialize initialValues={initialValues} onSubmit={() => {}}>
            {({ values, handleChange }) => (
              <div className="space-y-24">
                {offerOptions.map((option) => (
                  <div key={option.value} className={`mb-8 flex items-center`}>
                    <Field
                      type="radio"
                      name="selectedOffer"
                      id={option.value}
                      value={option.value}
                      checked={values.selectedOffer == option.value}
                      onChange={handleChange}
                      className="hidden"
                    />
                    <label
                      htmlFor={option.value}
                      className={`flex w-full cursor-pointer items-center font-suisse-intl text-description-sm text-primary-black outline-none ${option.labelClassName}`}
                    >
                      <div
                        className={clsx(
                          'mr-8 flex h-24 w-24 items-center justify-center rounded-full border border-solid border-black p-4',
                          option.radioButtonContainerClassName
                        )}
                      >
                        <span
                          className={clsx(
                            'h-full w-full rounded-full bg-primary-magenta',
                            option.radioButtonClassName,
                            {
                              block: values.selectedOffer === option.value,
                              hidden: values.selectedOffer !== option.value,
                            }
                          )}
                        />
                      </div>
                      <div>{option.children && <div>{option.children}</div>}</div>
                    </label>
                  </div>
                ))}
                <div className="mt-24">
                  {(() => {
                    const SubscribeButton =
                      values.selectedOffer === ANNUAL_OFFER ? PrimaryButton : ProButton;
                    return (
                      <SubscribeButton
                        as="link"
                        className="!w-auto px-16 py-8 "
                        size="xsmall"
                        href={subscribe_promotion_path({ offer: values.selectedOffer })}
                        onClickCapture={() =>
                          trackEvent(
                            click({
                              clickTarget: ClickTarget.Subscribe,
                              offerCode: values.selectedOffer,
                            })
                          )
                        }
                      >
                        Subscribe now
                      </SubscribeButton>
                    );
                  })()}
                </div>
              </div>
            )}
          </Formik>
        </div>
      </aside>
    </Track>
  );
};

export default SubscribeDropdownContent;
